import { percentageTransform } from '@unistocks/components/form/validation-utils';
import {
  applyRefinements,
  createDateSchema,
  createMoreThanSchema,
  createNumberSchema,
  requiredSchema
} from '@unistocks/components/form/validators';
import { t } from '@unistocks/shared/localization/index.ts';
import { Logger } from '@unistocks/shared/services/logger.service';
import { createDate, isDefined, isNumber } from '@unistocks/shared/utils/util';
import { z } from 'zod';

export const savingsGoalSchemaFactory = () =>
  applyRefinements(
    z.object({      name: requiredSchema(z.string(), t('::Name')),
      portfolios: requiredSchema(
        z
          .array(z.string())
          .min(1, t('::ValidationError:Required:Selection', { 0: t('::Portfolios') })),
        t('::Portfolios')
      ),
      currency: z.string().nullish(),
      startDate: requiredSchema(createDateSchema(), t('::SavingsGoal:StartDate')),
      endDate: requiredSchema(createDateSchema(), t('::SavingsGoal:EndDate')),
      goalValue: requiredSchema(
        createMoreThanSchema(0, t('::SavingsGoal:Amount')),
        t('::SavingsGoal:Amount')
      ),
      contribution: createNumberSchema(),
      annualReturns: z
        .union([z.number(), z.string(), z.null(), z.undefined()])
        .transform((originalValue, ctx) => {
          // The value has not been touched, return the original value
          if (isNumber(originalValue)) return originalValue;
          if (!isDefined(originalValue)) return null;

          // PercentageTransform is used to convert the input value to a percentage
          const parsed = percentageTransform.output?.(originalValue);

          if (isNumber(parsed)) return parsed;

          ctx.addIssue({ code: 'custom', message: t('::ValidationError:Number') });

          return z.NEVER;
        })
    }),
    (values, ctx) => {
      const { startDate, endDate } = values;

      if (!startDate || !endDate) return;

      try {
        if (createDate(startDate) < createDate(endDate)) return;
      } catch (e) {
        // createDate throws on invalid dates. Field level validation reports those separately.
        Logger.debug('Failed to validate savings goal dates', e);

        return;
      }

      ctx.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: t('::UnifyStocks.SavingsGoal:0002')
      });
    }
  ).loose();

export const annualReturnsTransform: typeof percentageTransform = {
  input: percentageTransform.input
};
